import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Send, User, Mail, Clock, CheckCircle2, XCircle, Paperclip, UserPlus, UserMinus, RefreshCw, Check, Eye, MousePointer, AlertOctagon, FileText, Image, X, Upload, Trash2 } from 'lucide-react';

var MSG_STATUS_CONFIG = {
  'pending': { className: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Ausstehend' },
  'sent': { className: 'bg-blue-500/20 text-blue-400', icon: Send, label: 'Gesendet' },
  'delivered': { className: 'bg-green-500/20 text-green-400', icon: Check, label: 'Zugestellt' },
  'opened': { className: 'bg-emerald-500/20 text-emerald-400', icon: Eye, label: 'Geöffnet' },
  'clicked': { className: 'bg-cyan-500/20 text-cyan-400', icon: MousePointer, label: 'Geklickt' },
  'bounced': { className: 'bg-red-500/20 text-red-400', icon: AlertOctagon, label: 'Zurückgewiesen' },
  'failed': { className: 'bg-red-600/20 text-red-500', icon: XCircle, label: 'Fehlgeschlagen' }
};

export var AdminTicketDetail = function() {
  var params = useParams();
  var ticketId = params.ticketId;
  var navigate = useNavigate();
  var auth = useAdminAuth();
  var token = auth.token;
  
  var ticketState = useState(null);
  var ticket = ticketState[0];
  var setTicket = ticketState[1];
  
  var messagesState = useState([]);
  var messages = messagesState[0];
  var setMessages = messagesState[1];
  
  var attachmentsState = useState({});
  var attachments = attachmentsState[0];
  var setAttachments = attachmentsState[1];
  
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var sendingState = useState(false);
  var sending = sendingState[0];
  var setSending = sendingState[1];
  
  var updatingState = useState(false);
  var updating = updatingState[0];
  var setUpdating = updatingState[1];
  
  var replyState = useState('');
  var replyText = replyState[0];
  var setReplyText = replyState[1];
  
  // Preview modal state
  var previewState = useState({ open: false, url: '', type: '', filename: '' });
  var preview = previewState[0];
  var setPreview = previewState[1];
  
  // File upload state
  var uploadingState = useState(false);
  var uploading = uploadingState[0];
  var setUploading = uploadingState[1];
  
  var pendingAttachmentsState = useState([]);
  var pendingAttachments = pendingAttachmentsState[0];
  var setPendingAttachments = pendingAttachmentsState[1];
  
  var fileInputRef = useRef(null);
  var autoRefreshRef = useRef(null);
  var backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Delete confirmation state
  var deleteDialogState = useState(false);
  var showDeleteDialog = deleteDialogState[0];
  var setShowDeleteDialog = deleteDialogState[1];
  
  var deletingState = useState(false);
  var deleting = deletingState[0];
  var setDeleting = deletingState[1];

  var fetchTicketSilent = useCallback(function() {
    var url = backendUrl + '/api/admin/tickets/' + ticketId;
    return fetch(url, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          setTicket(data.ticket);
          setMessages(data.messages || []);
          setAttachments(data.attachments || {});
        });
      }
    }).catch(function(error) {
      console.error('Silent fetch error:', error);
    });
  }, [backendUrl, ticketId, token]);

  var fetchTicket = function() {
    setLoading(true);
    var url = backendUrl + '/api/admin/tickets/' + ticketId;
    return fetch(url, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          setTicket(data.ticket);
          setMessages(data.messages || []);
          setAttachments(data.attachments || {});
        });
      } else {
        toast.error('Ticket nicht gefunden');
        navigate('/admin/tickets');
      }
    }).catch(function() {
      toast.error('Fehler beim Laden');
    }).finally(function() {
      setLoading(false);
    });
  };

  useEffect(function() {
    fetchTicket();
  }, [ticketId]);

  useEffect(function() {
    autoRefreshRef.current = setInterval(function() {
      fetchTicketSilent();
    }, 10000);
    return function() {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [fetchTicketSilent]);

  var handleFileSelect = function(e) {
    var files = Array.from(e.target.files || []);
    var allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    
    files.forEach(function(file) {
      if (!allowedTypes.includes(file.type)) {
        toast.error('Nur Bilder und PDFs erlaubt: ' + file.name);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Datei zu groß (max 10MB): ' + file.name);
        return;
      }
      
      // Upload file
      setUploading(true);
      var formData = new FormData();
      formData.append('file', file);
      
      fetch(backendUrl + '/api/admin/tickets/' + ticketId + '/attachments', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData
      }).then(function(response) {
        if (response.ok) {
          return response.json().then(function(data) {
            setPendingAttachments(function(prev) {
              return prev.concat([{
                id: data.attachment_id,
                filename: data.filename,
                size: data.size,
                content_type: data.content_type
              }]);
            });
            toast.success('Anhang hochgeladen');
          });
        } else {
          return response.json().then(function(data) {
            toast.error(data.detail || 'Upload fehlgeschlagen');
          });
        }
      }).catch(function() {
        toast.error('Upload fehlgeschlagen');
      }).finally(function() {
        setUploading(false);
      });
    });
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  var handleSendReply = function() {
    if (!replyText.trim()) {
      toast.error('Bitte gib eine Nachricht ein');
      return;
    }
    setSending(true);
    var url = backendUrl + '/api/admin/tickets/' + ticketId + '/reply';
    var attachmentIds = pendingAttachments.map(function(a) { return a.id; });
    
    fetch(url, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ body_plain: replyText, attachment_ids: attachmentIds })
    }).then(function(response) {
      if (response.ok) {
        toast.success('Antwort gesendet');
        setReplyText('');
        setPendingAttachments([]);
        fetchTicketSilent();
      } else {
        return response.json().then(function(data) {
          toast.error(data.detail || 'Fehler');
        });
      }
    }).catch(function() {
      toast.error('Fehler beim Senden');
    }).finally(function() {
      setSending(false);
    });
  };

  var handleUpdateTicket = function(updates) {
    setUpdating(true);
    var url = backendUrl + '/api/admin/tickets/' + ticketId;
    fetch(url, {
      method: 'PATCH',
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    }).then(function(response) {
      if (response.ok) {
        toast.success('Ticket aktualisiert');
        fetchTicketSilent();
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    }).catch(function() {
      toast.error('Fehler');
    }).finally(function() {
      setUpdating(false);
    });
  };
  
  var handleDeleteTicket = function() {
    setDeleting(true);
    var url = backendUrl + '/api/admin/tickets/' + ticketId;
    fetch(url, {
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          toast.success(data.message || 'Ticket gelöscht');
          navigate('/admin/tickets');
        });
      } else {
        return response.json().then(function(data) {
          toast.error(data.detail || 'Fehler beim Löschen');
        });
      }
    }).catch(function() {
      toast.error('Verbindungsfehler');
    }).finally(function() {
      setDeleting(false);
      setShowDeleteDialog(false);
    });
  };

  var openAttachmentPreview = function(attachmentId) {
    var att = attachments[attachmentId];
    if (!att) return;
    
    var isImage = att.content_type.startsWith('image/');
    var isPdf = att.content_type === 'application/pdf';
    
    if (isImage || isPdf) {
      setPreview({
        open: true,
        url: backendUrl + '/api/attachments/' + attachmentId,
        type: isImage ? 'image' : 'pdf',
        filename: att.filename
      });
    }
  };

  var removePendingAttachment = function(id) {
    setPendingAttachments(function(prev) {
      return prev.filter(function(a) { return a.id !== id; });
    });
  };

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center py-20' },
      React.createElement(Loader2, { className: 'h-8 w-8 animate-spin text-blue-500' })
    );
  }

  if (!ticket) {
    return React.createElement('div', { className: 'text-center py-20' },
      React.createElement('p', { className: 'text-slate-500' }, 'Ticket nicht gefunden'),
      React.createElement(Button, { asChild: true, variant: 'link', className: 'text-blue-400 mt-4' },
        React.createElement(Link, { to: '/admin/tickets' }, 'Zurück')
      )
    );
  }

  // Build message elements
  var messageElements = messages.map(function(message, index) {
    var isInbound = message.direction === 'inbound';
    var msgStatus = MSG_STATUS_CONFIG[message.message_status] || MSG_STATUS_CONFIG['pending'];
    var StatusIcon = msgStatus.icon;
    
    // Build status badges - show multiple if clicked (opened + clicked)
    var statusBadges = null;
    if (!isInbound) {
      var badges = [];
      
      // If clicked, show both "Geöffnet" and "Geklickt" badges
      if (message.message_status === 'clicked') {
        var openedConfig = MSG_STATUS_CONFIG['opened'];
        var OpenedIcon = openedConfig.icon;
        badges.push(
          React.createElement(Badge, { key: 'opened', className: 'text-xs ' + openedConfig.className },
            React.createElement(OpenedIcon, { className: 'h-3 w-3 mr-1' }),
            openedConfig.label
          )
        );
        badges.push(
          React.createElement(Badge, { key: 'clicked', className: 'text-xs ' + msgStatus.className },
            React.createElement(StatusIcon, { className: 'h-3 w-3 mr-1' }),
            msgStatus.label
          )
        );
      } else {
        // Single badge for other statuses
        badges.push(
          React.createElement(Badge, { key: 'status', className: 'text-xs ' + msgStatus.className },
            React.createElement(StatusIcon, { className: 'h-3 w-3 mr-1' }),
            msgStatus.label
          )
        );
      }
      
      statusBadges = React.createElement('div', { className: 'flex gap-1 mt-1 flex-wrap justify-end' }, badges);
    }
    
    // Format time in Europe/Berlin timezone
    var formattedTime = new Date(message.created_at).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Attachment elements
    var attachmentElements = null;
    if (message.attachments && message.attachments.length > 0) {
      var attItems = message.attachments.map(function(attId) {
        var att = attachments[attId];
        if (!att) return null;
        
        var isImage = att.content_type.startsWith('image/');
        var isPdf = att.content_type === 'application/pdf';
        var AttIcon = isImage ? Image : FileText;
        
        return React.createElement('button', {
          key: attId,
          onClick: function() { openAttachmentPreview(attId); },
          className: 'flex items-center gap-2 bg-slate-600/50 hover:bg-slate-500/50 px-3 py-2 rounded text-xs text-slate-300 transition-colors cursor-pointer'
        },
          React.createElement(AttIcon, { className: 'h-4 w-4' }),
          React.createElement('span', { className: 'truncate max-w-32' }, att.filename),
          React.createElement('span', { className: 'text-slate-500' }, '(' + Math.round(att.size / 1024) + ' KB)')
        );
      }).filter(Boolean);
      
      if (attItems.length > 0) {
        attachmentElements = React.createElement('div', { className: 'mt-3 pt-3 border-t border-slate-600/50' },
          React.createElement('p', { className: 'text-slate-400 text-xs mb-2 flex items-center gap-1' },
            React.createElement(Paperclip, { className: 'h-3 w-3' }),
            message.attachments.length + ' Anhang/Anhänge - Klicken für Vorschau'
          ),
          React.createElement('div', { className: 'flex flex-wrap gap-2' }, attItems)
        );
      }
    }
    
    return React.createElement('div', {
      key: message.id,
      className: 'p-4 rounded-lg ' + (isInbound ? 'bg-slate-700/50 border border-slate-600' : 'bg-blue-900/30 border border-blue-700/50'),
      'data-testid': 'ticket-message-' + index
    },
      React.createElement('div', { className: 'flex items-start justify-between mb-3' },
        React.createElement('div', { className: 'flex items-center gap-2' },
          React.createElement('div', { className: 'w-8 h-8 rounded-full flex items-center justify-center ' + (isInbound ? 'bg-slate-600' : 'bg-blue-600') },
            isInbound ? React.createElement(User, { className: 'h-4 w-4 text-white' }) : React.createElement(Mail, { className: 'h-4 w-4 text-white' })
          ),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-white font-medium text-sm' }, message.from_name || message.from_email),
            React.createElement('p', { className: 'text-slate-500 text-xs' }, message.from_email)
          )
        ),
        React.createElement('div', { className: 'text-right' },
          React.createElement('p', { className: 'text-slate-400 text-xs' }, formattedTime),
          statusBadges
        )
      ),
      React.createElement('div', { className: 'text-slate-300 whitespace-pre-wrap text-sm leading-relaxed' }, message.body_plain),
      attachmentElements
    );
  });

  // Pending attachments for reply
  var pendingAttachmentElements = pendingAttachments.length > 0 ? 
    React.createElement('div', { className: 'flex flex-wrap gap-2 mb-4' },
      pendingAttachments.map(function(att) {
        var isImage = att.content_type.startsWith('image/');
        var AttIcon = isImage ? Image : FileText;
        return React.createElement('div', {
          key: att.id,
          className: 'flex items-center gap-2 bg-blue-900/30 border border-blue-700/50 px-3 py-2 rounded text-xs text-slate-300'
        },
          React.createElement(AttIcon, { className: 'h-4 w-4' }),
          React.createElement('span', null, att.filename),
          React.createElement('button', {
            onClick: function() { removePendingAttachment(att.id); },
            className: 'ml-1 text-red-400 hover:text-red-300'
          },
            React.createElement(X, { className: 'h-3 w-3' })
          )
        );
      })
    ) : null;

  // Preview Modal
  var previewModal = React.createElement(Dialog, {
    open: preview.open,
    onOpenChange: function(open) { setPreview(function(p) { return { ...p, open: open }; }); }
  },
    React.createElement(DialogContent, { className: 'bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[90vh]' },
      React.createElement(DialogHeader, null,
        React.createElement(DialogTitle, { className: 'text-white flex items-center gap-2' },
          preview.type === 'image' ? React.createElement(Image, { className: 'h-5 w-5' }) : React.createElement(FileText, { className: 'h-5 w-5' }),
          preview.filename
        )
      ),
      React.createElement('div', { className: 'mt-4 overflow-auto max-h-[70vh]' },
        preview.type === 'image' ?
          React.createElement('img', {
            src: preview.url,
            alt: preview.filename,
            className: 'max-w-full h-auto rounded'
          }) :
          React.createElement('iframe', {
            src: preview.url,
            className: 'w-full h-[600px] rounded bg-white'
          })
      )
    )
  );

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'admin-ticket-detail' },
    previewModal,
    React.createElement('div', { className: 'flex items-start justify-between' },
      React.createElement('div', null,
        React.createElement(Button, { asChild: true, variant: 'ghost', size: 'sm', className: 'text-slate-400 hover:text-white mb-2' },
          React.createElement(Link, { to: '/admin/tickets' },
            React.createElement(ArrowLeft, { className: 'h-4 w-4 mr-2' }),
            'Zurück'
          )
        ),
        React.createElement('h1', { className: 'text-2xl font-bold text-white' }, ticket.ticket_number),
        React.createElement('h2', { className: 'text-lg text-slate-300 mt-1' }, ticket.subject)
      ),
      React.createElement('div', { className: 'flex items-center gap-2' },
        React.createElement('span', { className: 'text-xs text-slate-500' }, 'Auto-Refresh (10s)'),
        React.createElement(Button, { variant: 'outline', size: 'sm', onClick: fetchTicketSilent, className: 'border-slate-600 text-slate-300' },
          React.createElement(RefreshCw, { className: 'h-4 w-4 mr-2' }),
          'Aktualisieren'
        ),
        React.createElement(Button, { 
          variant: 'outline', 
          size: 'sm', 
          onClick: function() { setShowDeleteDialog(true); },
          className: 'border-red-600/50 text-red-400 hover:bg-red-900/30 hover:text-red-300'
        },
          React.createElement(Trash2, { className: 'h-4 w-4' })
        )
      )
    ),
    // Delete Confirmation Dialog
    React.createElement(Dialog, { open: showDeleteDialog, onOpenChange: setShowDeleteDialog },
      React.createElement(DialogContent, { className: 'bg-slate-800 border-slate-700 text-white' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'text-white flex items-center gap-2' },
            React.createElement(Trash2, { className: 'h-5 w-5 text-red-400' }),
            'Ticket löschen'
          ),
          React.createElement(DialogDescription, { className: 'text-slate-400' },
            'Bist du sicher, dass du dieses Ticket löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden. Alle Nachrichten und Anhänge werden ebenfalls gelöscht.'
          )
        ),
        React.createElement(DialogFooter, { className: 'mt-4' },
          React.createElement(Button, { variant: 'outline', onClick: function() { setShowDeleteDialog(false); }, className: 'border-slate-600 text-slate-300' }, 'Abbrechen'),
          React.createElement(Button, { 
            variant: 'destructive', 
            onClick: handleDeleteTicket, 
            disabled: deleting,
            className: 'bg-red-600 hover:bg-red-700'
          },
            deleting ? React.createElement(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) : React.createElement(Trash2, { className: 'h-4 w-4 mr-2' }),
            'Endgültig löschen'
          )
        )
      )
    ),
    React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-3 gap-6' },
      React.createElement('div', { className: 'lg:col-span-2 space-y-4' },
        // Reply Card
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, { className: 'pb-3' },
            React.createElement(CardTitle, { className: 'text-white text-lg flex items-center gap-2' },
              React.createElement(Send, { className: 'h-5 w-5 text-blue-400' }),
              'Antwort senden'
            )
          ),
          React.createElement(CardContent, null,
            React.createElement(Textarea, {
              value: replyText,
              onChange: function(e) { setReplyText(e.target.value); },
              placeholder: 'Deine Antwort an den Kunden...',
              className: 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 min-h-32 mb-4',
              'data-testid': 'ticket-reply-textarea'
            }),
            pendingAttachmentElements,
            React.createElement('div', { className: 'flex items-center justify-between' },
              React.createElement('div', { className: 'flex items-center gap-2' },
                React.createElement('input', {
                  type: 'file',
                  ref: fileInputRef,
                  onChange: handleFileSelect,
                  accept: 'image/*,.pdf',
                  multiple: true,
                  className: 'hidden'
                }),
                React.createElement(Button, {
                  variant: 'outline',
                  size: 'sm',
                  onClick: function() { fileInputRef.current && fileInputRef.current.click(); },
                  disabled: uploading,
                  className: 'border-slate-600 text-slate-300'
                },
                  uploading ? React.createElement(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) : React.createElement(Paperclip, { className: 'h-4 w-4 mr-2' }),
                  'Anhang'
                )
              ),
              React.createElement(Button, {
                onClick: handleSendReply,
                disabled: sending || !replyText.trim(),
                className: 'bg-blue-600 hover:bg-blue-700',
                'data-testid': 'ticket-send-reply-btn'
              },
                sending ? React.createElement(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) : React.createElement(Send, { className: 'h-4 w-4 mr-2' }),
                'Antwort senden'
              )
            )
          )
        ),
        // Conversation Card
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'text-white text-lg' }, 'Konversation (' + messages.length + ' Nachrichten)')
          ),
          React.createElement(CardContent, { className: 'space-y-4' },
            messages.length === 0 ?
              React.createElement('p', { className: 'text-slate-500 text-center py-8' }, 'Keine Nachrichten') :
              messageElements
          )
        )
      ),
      // Sidebar
      React.createElement('div', { className: 'space-y-4' },
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'text-white text-lg' }, 'Ticket-Details')
          ),
          React.createElement(CardContent, { className: 'space-y-4' },
            React.createElement('div', null,
              React.createElement('label', { className: 'text-slate-400 text-sm mb-2 block' }, 'Status'),
              React.createElement(Select, { value: ticket.status, onValueChange: function(v) { handleUpdateTicket({ status: v }); }, disabled: updating },
                React.createElement(SelectTrigger, { className: 'bg-slate-700 border-slate-600 text-white' },
                  React.createElement(SelectValue, null)
                ),
                React.createElement(SelectContent, { className: 'bg-slate-800 border-slate-700' },
                  React.createElement(SelectItem, { value: 'open' }, 'Offen'),
                  React.createElement(SelectItem, { value: 'in_progress' }, 'In Bearbeitung'),
                  React.createElement(SelectItem, { value: 'waiting_customer' }, 'Wartet auf Kunde'),
                  React.createElement(SelectItem, { value: 'resolved' }, 'Gelöst'),
                  React.createElement(SelectItem, { value: 'closed' }, 'Geschlossen')
                )
              )
            ),
            React.createElement('div', null,
              React.createElement('label', { className: 'text-slate-400 text-sm mb-2 block' }, 'Priorität'),
              React.createElement(Select, { value: ticket.priority, onValueChange: function(v) { handleUpdateTicket({ priority: v }); }, disabled: updating },
                React.createElement(SelectTrigger, { className: 'bg-slate-700 border-slate-600 text-white' },
                  React.createElement(SelectValue, null)
                ),
                React.createElement(SelectContent, { className: 'bg-slate-800 border-slate-700' },
                  React.createElement(SelectItem, { value: 'low' }, 'Niedrig'),
                  React.createElement(SelectItem, { value: 'normal' }, 'Normal'),
                  React.createElement(SelectItem, { value: 'high' }, 'Hoch'),
                  React.createElement(SelectItem, { value: 'urgent' }, 'Dringend')
                )
              )
            ),
            React.createElement('div', null,
              React.createElement('label', { className: 'text-slate-400 text-sm mb-2 block' }, 'Zugewiesen an'),
              ticket.assigned_name ?
                React.createElement('div', { className: 'space-y-2' },
                  React.createElement('p', { className: 'text-white text-sm bg-slate-700 px-3 py-2 rounded' }, ticket.assigned_name),
                  React.createElement(Button, { variant: 'outline', size: 'sm', onClick: function() { handleUpdateTicket({ unassign: true }); }, disabled: updating, className: 'border-slate-600 text-slate-300 w-full' },
                    React.createElement(UserMinus, { className: 'h-4 w-4 mr-2' }),
                    'Zuweisung aufheben'
                  )
                ) :
                React.createElement(Button, { variant: 'outline', size: 'sm', onClick: function() { handleUpdateTicket({ assign_to_me: true }); }, disabled: updating, className: 'border-slate-600 text-slate-300 w-full' },
                  React.createElement(UserPlus, { className: 'h-4 w-4 mr-2' }),
                  'Mir zuweisen'
                )
            )
          )
        ),
        React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, { className: 'text-white text-lg flex items-center gap-2' },
              React.createElement(User, { className: 'h-5 w-5 text-blue-400' }),
              'Kunde'
            )
          ),
          React.createElement(CardContent, { className: 'space-y-3' },
            React.createElement('div', null,
              React.createElement('label', { className: 'text-slate-400 text-xs' }, 'Name'),
              React.createElement('p', { className: 'text-white' }, ticket.customer_name || 'Unbekannt')
            ),
            React.createElement('div', null,
              React.createElement('label', { className: 'text-slate-400 text-xs' }, 'E-Mail'),
              React.createElement('p', { className: 'text-white text-sm break-all' }, ticket.customer_email)
            )
          )
        )
      )
    )
  );
};
